/**
 * Agency-record domain (distinct from context/agency.js, which owns the
 * CURRENT session's agency doc) - built on apiClient via useRequest, not
 * useAppServices(). These are the ad-hoc agency.* lookups/mutations several
 * unrelated pages make, each kept as its own hook with the same queryKey the
 * old inline useQuery already used at that call site:
 *   - AccountDetail.js -> useAgencyByPlanId (["agency-plan", planId])
 *   - Accounts/components/data-tables/data/dataTableData.js ->
 *     useStripeProducts (["stripe-products"])
 *   - AgentFramework/index.js -> useApplyDefaultAgencySettings
 *   - Assistants/Components/CreateModal/index.js -> useAgentBuilderData
 *   - MaybeConnection/index.js -> useAgencyById (["agency-connection", id])
 *   - settings/billing/index.js -> useAgencyChargeLogs (["charge-logs", ...])
 *   - settings/billing/components/AddBalance/index.js -> useAddAgencyBalance
 *   - settings/billing/components/Balance/index.js,
 *     settings/Configurator/{index,components/BasicInfo/BrandingTabs/*}.js,
 *     settings/Integration/components/StripeCoonection/index.js ->
 *     useUpdateAgency (generic PUT /agency, shared - all invalidate ["agency"]
 *     so every useAgencyInfo() consumer refreshes)
 *   - settings/Integration/components/StripeCoonection/index.js ->
 *     useUpdateAgencyStripe
 *   - UiPrompts/JobRoles/index.js, UiPrompts/Sectors/index.js ->
 *     useUpdateAgencyCustomFieldOptions
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT } = ReqMethods;

export function useAgencyById(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["agency-connection", agencyId],
    queryFn: async () => {
      const { response } = await request(GET, "/agency", { query: `_id=${agencyId}` });
      return response ? { ...response.data } : {};
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useAgencyByPlanId(planId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["agency-plan", planId],
    queryFn: async () => {
      const { response } = await request(GET, "/agency/getbyplan_byId", {
        query: `_id=${planId}`,
      });
      return response?.data || {};
    },
    enabled: !!planId,
    ...options,
  });
}

// toaster:false preserved - this endpoint is a known 404 in some agency
// configs and the old call site silently swallowed that (returns [] below).
export function useStripeProducts(options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["stripe-products"],
    queryFn: async () => {
      const { response } = await request(GET, "/agency/getproducts", { toaster: false });
      if (!response) return [];
      const newArray = response.data.data.filter((item) => item.active === true);
      return newArray.map((product) => {
        const label =
          (product.unit_amount / 100).toFixed(2) +
          (product.nickname ? " ( " + product.nickname + " ) " : "") +
          " - " +
          product.id;
        return { label, value: label };
      });
    },
    ...options,
  });
}

export function useAgencyChargeLogs(agencyId, page, limit = 10, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["charge-logs", agencyId, page],
    queryFn: async () => {
      const { response, error } = await request(GET, "/agency/chargelogs", {
        query: `agency_id=${agencyId}&level=agency&page=${page}&limit=${limit}`,
      });
      if (error) throw error;
      return response;
    },
    enabled: !!agencyId,
    ...options,
  });
}

export function useApplyDefaultAgencySettings() {
  const request = useRequest();
  return useMutation({
    mutationFn: (agency_id) =>
      request(POST, `/agency/${agency_id}/apply-default-settings`, { agency_id }),
  });
}

export function useAgentBuilderData() {
  const request = useRequest();
  return useMutation({
    mutationFn: (payload) => request(POST, "/agency/use-agent-builder", { payload }),
  });
}

// `vars` is forwarded as-is to request() (the old AppService.agency.AddBalance
// did the same - it's whatever options bag the one caller builds). Adding
// balance also writes a charge-log row, so both lists refresh on success,
// same as the inline useMutation this replaces.
export function useAddAgencyBalance() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars) => request(PUT, "/agency/addbalance", vars),
    onSuccess: ({ response }) => {
      if (response) {
        queryClient.invalidateQueries({ queryKey: ["agency"] });
        queryClient.invalidateQueries({ queryKey: ["charge-logs"] });
      }
    },
  });
}

// Generic PUT /agency - the single most-reused agency mutation across the
// app. `toaster` is a hook-call-time param (not per-mutate-call) since each
// existing call site hardcodes its own value the same way - mutate(payload)
// keeps working exactly like the old mutate(formData) did.
export function useUpdateAgency(toaster = false) {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/agency", { payload, toaster }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agency"] }),
  });
}

export function useUpdateAgencyStripe() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/agency/update_stripe", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agency"] }),
  });
}

export function useUpdateAgencyCustomFieldOptions() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/agency/update-customfield-options", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["agency"] }),
  });
}
